import {
  faArrowRightToBracket,
  faLockAlt,
  faStar,
  faTrashAlt,
  faUser,
  faLanguage,
  faWrench,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { profileColours } from '../../../assets/colours';
import LoadingSpinner from '../../../shared/components/UIElements/LoadingSpinner';
import { AuthContext } from '../../../shared/context/auth-context';
import { ActionButton } from '../../components/actionButton/ActionButton';
import { MenuOptionButton } from '../../components/menuOptionButton/MenuOptionButton';
import { MenuOptionLink } from '../../components/menuOptionLink/MenuOptionLink';
import { ImageUploadBtn } from './ImageUploadBtn';
import './AccountDetails.css';

type AccountDetailsLeftMenuProps = {
  profile: any;
  changeMenu: (arg0: number, arg1?: string) => void;
  activeSubMenu: { id: number };
  changeSubMenu: (arg0: number) => void;
  isHidden: boolean;
  saveProfileImage: (url: string) => void;
};

export function AccountDetailsLeftMenu({
  profile,
  changeMenu,
  activeSubMenu,
  changeSubMenu,
  isHidden,
  saveProfileImage,
}: AccountDetailsLeftMenuProps) {
  const auth = useContext(AuthContext);
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  if (!profile) return null;

  return (
    <>
      <ProfilePicture saveProfileImage={saveProfileImage} />
      <h2 className="profile-name fs-exclude">{profile.name}</h2>
      <MenuOptionButton
        id="account-details"
        icon={faUser}
        color={profileColours.blue}
        text={t('S025')}
        onClick={() => changeSubMenu(0)}
        isActive={activeSubMenu.id === 51 && !isHidden}
      />
      {(auth.authType == null || auth.authType === 'ChampionHealth') && (
        <MenuOptionButton
          id="security-settings"
          icon={faLockAlt}
          color={profileColours.yellow}
          text={t('S126')}
          onClick={() => changeMenu(1)}
          isActive={activeSubMenu.id === 52 && !isHidden}
        />
      )}
      <MenuOptionButton
        id="technical-support"
        icon={faLanguage}
        color={profileColours.green}
        text={t('PL001')}
        onClick={() => changeSubMenu(6)}
        isActive={activeSubMenu.id === 57 && !isHidden}
      />
      <MenuOptionLink
        to="https://support.championhealth.co.uk/"
        id="technical-support"
        icon={faWrench}
        color={profileColours.yellow}
        text={t('H004')}
        isActive={false}
        isNewTab
        ariaLabel={`${t('H004')} - open in new tab`}
      />
      <div className="profile-action-btn-container">
        {auth.clientName && (
          <a
            href={`https://physitrack.typeform.com/first-feedback#client=${auth.clientName.replace(
              / /,
              '_'
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="profile-action-link"
          >
            <FontAwesomeIcon icon={faStar} className="action-icon" />

            {t('S005')}
          </a>
        )}

        <ActionButton
          color={profileColours.white}
          onClick={auth.logout}
          icon={faArrowRightToBracket}
          text={t('S006')}
        />
        <ActionButton
          onClick={() => changeSubMenu(3)}
          icon={faTrashAlt}
          text={t('S007')}
          color={profileColours.red}
        />
      </div>
      <ProfileFooter year={currentYear} t={t} />
    </>
  );
}

function ProfilePicture({ saveProfileImage }) {
  const auth = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(true);

  return (
    <div className="profile-photo fs-exclude">
      {isLoading && <LoadingSpinner asImage />}
      <img
        id="preview"
        src={auth.image}
        alt=""
        onLoad={() => setIsLoading(false)}
        style={{ display: isLoading ? 'none' : 'block' }}
      />
      <ImageUploadBtn saveProfileImage={saveProfileImage} />
    </div>
  );
}

function ProfileFooter({ year, t }) {
  return (
    <div className="profile-footer">
      <div className="privacy">&copy; {year} Champion Health</div>
      <a
        href="https://www.championhealth.co.uk/legals/privacy-policy"
        target="_blank"
        rel="noopener noreferrer"
        className="profile-footer-link"
      >
        {t('S127')}
      </a>
      <a
        href="https://www.championhealth.co.uk/legals/terms-of-service"
        target="_blank"
        rel="noopener noreferrer"
        className="profile-footer-link"
      >
        {t('S131')}
      </a>
    </div>
  );
}
